import { z } from "zod";

/**
 * Zod schema for a single trigger condition with resource prefix support.
 *
 * Resource prefixes allow conditions to target specific resources:
 * - "file": targets file properties (requires for_each="dataset_file")
 * - "dataset": explicitly targets dataset properties
 */
export const triggerConditionSchema = z.object({
  id: z.string(),
  resourcePrefix: z.enum(["file", "dataset"]),
  field: z.string().min(1, "Field is required"),
  comparator: z.string(),
  value: z.string(),
  searchFieldType: z.enum(["date", "metadata", "tags", "other"]),
});

export type TriggerCondition = z.infer<typeof triggerConditionSchema>;

/**
 * Zod schema for the single condition input form.
 * This is used by TriggerConditionInput with react-hook-form.
 *
 * Note: We use optional fields with defaults because the form
 * builds up the condition incrementally as the user selects options.
 */
export const triggerConditionInputSchema = z.object({
  resourcePrefix: z.enum(["file", "dataset"]),
  selectedField: z.string().min(1, "Field is required"),
  comparator: z.string().min(1, "Comparator is required"),
  value: z.string().optional().default(""),
  // For date fields
  dateValue: z.date().nullable().optional(),
  // For metadata fields
  metadataKey: z.string().optional().default(""),
  metadataValue: z.string().optional().default(""),
  metadataType: z
    .enum(["string", "number", "boolean"])
    .optional()
    .default("string"),
});

export type TriggerConditionInputForm = z.infer<
  typeof triggerConditionInputSchema
>;
